import { PoolTypeMap, PoolTypes, WeeklyReportResponse } from '../../models/weekly-report.model';
import { Component, Inject, OnInit, OnDestroy, Renderer2 } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MaterialModule } from '../../material.module';
import { FormsModule } from '@angular/forms';
import { WeeklyReportService } from '../../services/weekly-report.service';
import { ModalSnackbarService } from '../../services/modal-snackbar.service';
import { firstValueFrom, lastValueFrom } from 'rxjs';
import { blankOpen, compareAccountNo, decimalCellRenderer, decimalCellRendererSuffixPer, decimalPercentCellRenderer, floor2dpString, formatAmount, formatDecimalTrim, getAdToYm, getNextAd, getPool, getPrevAd, numericComparator, rateToPercent, toNum } from '../../misc/functions';
import { AG_GRID_LOCALE_JP } from '../../misc/ag-grid-locale';
import { CellValueChangedEvent, ColDef, ColGroupDef, FirstDataRenderedEvent, GridApi, GridOptions, GridReadyEvent, ValueFormatterParams } from 'ag-grid-community';
import { CustomTextFilterComponent } from '../../misc/custom-filter.component';
import { AgGridModule } from 'ag-grid-angular';
import { User, UserService } from '../../services/user.service';
import { Account } from '../../models/account.model';
import { MonthlyReport, MonthlyReportIndex } from '../../models/monthly-report.model';
import { MonthlyReportService } from '../../services/monthly-report.service';
import { DOCUMENT } from '@angular/common';

@Component({
  selector: 'app-monthly-report',
  standalone: true,
  imports: [CommonModule, RouterModule, MaterialModule, FormsModule, AgGridModule],
  templateUrl: './monthly-report.component.html',
  styleUrls: ['./monthly-report.component.scss']
})
export class MonthlyReportComponent implements OnInit, OnDestroy  {
  private mainEl!: HTMLElement;

  isLoaded = false;
  localeText = AG_GRID_LOCALE_JP;
  blankOpen = blankOpen;

  currentUser!: User;
  accounts?: Account[] = undefined;

  vfTrim = (p: ValueFormatterParams) => formatDecimalTrim(p.value, 'ja-JP');
  toNum = toNum;
  numericComparator = numericComparator;
  rateToPercent = rateToPercent;
  getPool = getPool;
  compareAccountNo = compareAccountNo;

  uid: number = 0
  data: MonthlyReport[]|undefined = undefined;
  gridApi?: GridApi<MonthlyReport>;

  latestAd: number = 0;
  currentAd: number = 0;
  prevAd: number = 0;
  nextAd: number = 0;

  currentCapId = '';

  isAdmin = false;

  Modes = ['monthly', 'cap_id'] as const;
  mode: typeof this.Modes[number] = 'monthly';

  defaultColDef: ColDef = {
    filter: 'agTextColumnFilter',
    autoHeaderHeight: true,
    wrapHeaderText: true,
    floatingFilter: false,
    suppressHeaderMenuButton: true,
    suppressFloatingFilterButton: true,
    suppressFiltersToolPanel: true,
    floatingFilterComponent: CustomTextFilterComponent
  };

  columnDefs: ColDef[] = [
    { field: 'ym', headerName: '　月次', width: 80, pinned: 'left' },
    { field: 'cap_id', headerName: '口座番号', width: 105, pinned: 'left', comparator: this.compareAccountNo, sort: 'asc'},
    { field: 'pool', headerName: 'POOL', width: 95, pinned: 'left', valueFormatter: p => this.getPool(p.data.cap_id), filterValueGetter: p => this.getPool(p.data.cap_id)},
    { field: 'cur', headerName: '通貨', width: 55, pinned: 'left' },

    { field: 'hdc', headerName: '手数料(%)', width: 65, valueFormatter: this.vfTrim, cellClass: 'decimal-cell', comparator: this.numericComparator, filter: 'agNumberColumnFilter',
      valueGetter: p => this.rateToPercent(this.toNum(p.data.hdc)?? 0)},

    { field: 'monthly_management_fee', headerName: '月管理\n手数料(%)', width: 65, valueFormatter: this.vfTrim, cellClass: 'decimal-cell', comparator: this.numericComparator, filter: 'agNumberColumnFilter',
      valueGetter: p => this.switchVersion(p.data.period, this.rateToPercent(this.toNum(p.data.monthly_management_fee) ?? 0))},

    { field: 'bal_a', headerName: '先月末残高', width: 110, cellRenderer: decimalCellRenderer, valueFormatter: this.vfTrim, cellClass: 'decimal-cell', comparator: this.numericComparator, filter: 'agNumberColumnFilter',
      valueGetter: p => this.switchVersion(p.data.period, this.toNum(p.data.bal_a)) },

    { field: 'nyukin', headerName: '入金額', width: 110, cellRenderer: decimalCellRenderer, valueFormatter: this.vfTrim, cellClass: 'decimal-cell', comparator: this.numericComparator, filter: 'agNumberColumnFilter',
      filterValueGetter: p => this.toNum(p.data.nyukin)  },

    { field: 'adjust_amount', headerName: '調整額', width: 110, cellRenderer: decimalCellRenderer, valueFormatter: this.vfTrim, cellClass: 'decimal-cell', comparator: this.numericComparator, filter: 'agNumberColumnFilter',
      valueGetter: p => this.switchVersion(p.data.period, this.toNum(p.data.adjust_amount)) },

    { field: 'balance_start', headerName: '月初残高', width: 110, cellRenderer: decimalCellRenderer, valueFormatter: this.vfTrim, cellClass: 'decimal-cell', comparator: this.numericComparator, filter: 'agNumberColumnFilter',
      filterValueGetter: p => this.toNum(p.data.balance_start)  },

    { field: 'monthly_pl', headerName: '月間損益', width: 100, cellRenderer: decimalCellRenderer, valueFormatter: this.vfTrim, cellClass: 'decimal-cell', comparator: this.numericComparator, filter: 'agNumberColumnFilter',
      filterValueGetter: p => this.toNum(p.data.monthly_pl)  },

    { field: 'hc_a', headerName: '手数料\n（成功報酬）', width: 100, cellRenderer: decimalCellRenderer, valueFormatter: this.vfTrim, cellClass: 'decimal-cell', comparator: this.numericComparator, filter: 'agNumberColumnFilter',
      filterValueGetter: p => this.toNum(p.data.hc_a)  },

    { field: 'otherFees', headerName: '手数料\n（その他）', width: 100, cellRenderer: decimalCellRenderer, valueFormatter: this.vfTrim, cellClass: 'decimal-cell', comparator: this.numericComparator, filter: 'agNumberColumnFilter',
      filterValueGetter: p => this.toNum(p.data.hc_a)  },

    { field: 'totalShukkin', headerName: '出金・振替額', width: 110, cellRenderer: decimalCellRenderer, valueFormatter: this.vfTrim, cellClass: 'decimal-cell', comparator: this.numericComparator, filter: 'agNumberColumnFilter',
      filterValueGetter: p => this.toNum(p.data.shukkin)  },

    { field: 'bal_b', headerName: '月末残高', width: 110, cellRenderer: decimalCellRenderer, valueFormatter: this.vfTrim, cellClass: 'decimal-cell', comparator: this.numericComparator, filter: 'agNumberColumnFilter',
      filterValueGetter: p => this.toNum(p.data.bal_b)  },

    { field: 'monthly_per', headerName: '手数料前\n収益率\n(%)', width: 65, cellRenderer: decimalCellRenderer, valueFormatter: this.vfTrim, cellClass: 'decimal-cell', comparator: this.numericComparator, filter: 'agNumberColumnFilter',
      valueGetter: p => this.rateToPercent(this.toNum(p.data.monthly_per) ?? 0)},

      { field: 'rate_b', headerName: '手数料後\n収益率\n(%)', width: 65, cellRenderer: decimalCellRenderer, valueFormatter: this.vfTrim, cellClass: 'decimal-cell', comparator: this.numericComparator, filter: 'agNumberColumnFilter',
      valueGetter: p => this.rateToPercent(this.toNum(p.data.rate_b) ?? 0)},

    { field: 'enroll', headerName: '運用開始日', width: 100 },
    { field: 'chg', headerName: '資金拘束期間\n終了日', width: 100 },
    { field: 'cmm', headerName: '共同名義人', width: 120 }
  ];

  gridOptions: GridOptions = {
    rowClassRules: {
      'old-version': params => params.data.period <= '2025-06-01'
    }
  };

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private monthlyService: MonthlyReportService,
    private snackBar: ModalSnackbarService,
    private userService: UserService,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: Document
  ) {}

  ngOnInit(): void {
    const tmp = this.route.snapshot.queryParamMap.get('uid');
    this.uid = tmp ? parseInt(tmp) : 0;
    this.initialLoad();

    this.mainEl = this.document.getElementById('main')!;
    if (this.mainEl) {
      this.renderer.addClass(this.mainEl, 'main-fullwidth');
    }
  }

  ngOnDestroy(): void {
    if (this.mainEl) {
      this.renderer.removeClass(this.mainEl, 'main-fullwidth');
    }

    this.gridApi = null as any;
  }

  switchVersion(period: string, value: number|null) {
    return (period <= '2025-06-01') ? null : value;
  }

  get ym() {
    return getAdToYm(this.currentAd);
  }

  onGridReady(e: GridReadyEvent<MonthlyReport>) {
    this.gridApi = e.api;
  }

  export() {
    if (this.gridApi) {
      const adminInfo = this.isAdmin ? ` ${this.userinfo}` : '';
      const filename = (this.mode === 'monthly') ? '月次損益_' + this.ym + adminInfo + '.csv' : '口座損益_' + this.currentCapId + adminInfo + '.csv';
      this.gridApi.exportDataAsCsv({
        fileName: filename
      });
    }
  }

  async initialLoad() {

    this.isAdmin = (this.uid > 0);

    // uid パラメータが指定されていたら任意のユーザーの情報を出す（admin のみ）
    // getUserById は admin 以外がアクセスすると権限エラーで落ちる
    //                                                         admin のみの API                                         一般ユーザーの API
    const userRes = this.isAdmin ? await lastValueFrom(this.userService.getUserById(this.uid)) : await lastValueFrom(this.userService.getCurrentUser());
    this.currentUser = this.userService.parseData(userRes);

    this.latestAd = await firstValueFrom(this.monthlyService.getLatestAd());
    this.changeAd(this.latestAd);

    this.isLoaded = true;
  }

  onFirstDataRendered(e: FirstDataRenderedEvent) {
    //e.api.ensureColumnVisible('balance_start', 'start');
  }

  get userinfo() {
    if (!this.currentUser) return '';
    return `${this.currentUser.name}（${this.currentUser.login}）様　Email: ${this.currentUser.mail}`;
  }

  get title() {
    switch(this.mode) {
      case 'monthly':
        return this.ym + '（各行をダブルクリックで口座別の損益を表示）';
      case 'cap_id':
        return this.currentCapId + ' の月次履歴';
    }
  }

  changeAd(ad: number) {
    this.mode = 'monthly';
    this.data = undefined;

    this.currentAd = ad;
    this.prevAd = getPrevAd(ad);
    this.nextAd = getNextAd(ad);

    this.monthlyService.getByLoginIdAndAd(this.currentUser.login, ad, this.isAdmin).subscribe({
      next: (res) => {
        if (res) {
          this.data = res;

          if (res.length === 0) {
            this.snackBar.show(this.ym + `の月報はありません`);
          }

          queueMicrotask(() => {
            if (this.gridApi && !this.gridApi.isDestroyed()) {
              //this.gridApi.ensureColumnVisible('balance_start', 'start');
            }
          });
        }
      },
      error: () => this.snackBar.show(`月報の読み込みに失敗しました`)
    })
  }

  onRowClicked(event: any) {

    if (this.mode === 'cap_id') return;

    this.currentCapId = event.data.cap_id;
    this.mode = 'cap_id';
    this.data = undefined;

    this.monthlyService.getByLoginIdAndCapId(this.currentUser.login, this.currentCapId, this.isAdmin).subscribe({
      next: (res) => {
        if (res) {
          this.data = res;

          if (res.length === 0) {
            this.snackBar.show(this.currentCapId + `の月報はありません`);
          }

          queueMicrotask(() => {
            if (this.gridApi && !this.gridApi.isDestroyed()) {
              //this.gridApi.ensureColumnVisible('balance_start', 'start');
            }
          });
        }
      },
      error: () => this.snackBar.show(`月報の読み込みに失敗しました`)
    })

  }

}
